"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { type AccountType, type AuthUser } from "../types";
import { switchDependantApi } from "@/modules/dashboard/services/dependants.api";
import { notifyProfileSwitched, notifyUserChanged } from "@/modules/auth/utils/authToken";

export type AuthContextValue = {
  user: AuthUser;
  /**
   * The account's immutable context. There is no role switching any more — a
   * student account is always a student, a teacher account always a teacher.
   */
  accountType: AccountType;
  isSwitchingDependant: boolean;
  switchingTargetId: string | null;
  switchDependant: (dependantId: string) => Promise<void>;
  replaceUser: (user: AuthUser) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthProviderProps = {
  user: AuthUser;
  children: ReactNode;
};

function resolveAccountType(user: AuthUser): AccountType {
  return user.account_type === "teacher" ? "teacher" : "student";
}

export function AuthProvider({ user, children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<AuthUser>(user);
  const [isSwitchingDependant, setIsSwitchingDependant] = useState(false);
  const [switchingTargetId, setSwitchingTargetId] = useState<string | null>(null);

  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  // Switching between a family's student profiles/dependants. This is entirely
  // orthogonal to account_type: every switchable profile is itself a student
  // account, so the context stays "student" throughout.
  const switchDependant = useCallback(
    async (dependantId: string) => {
      setIsSwitchingDependant(true);
      setSwitchingTargetId(dependantId);

      try {
        const data = await switchDependantApi(dependantId);
        setCurrentUser(data.user);
        // NOTE: switchDependantApi() persists a brand-new token
        // (storeAuthToken -> notifyAuthTokenChanged), which already makes the
        // root useUser() refetch /api/me and keeps the root-level cache in
        // sync. Firing notifyUserChanged() too would issue a second, redundant
        // /api/me for a single switch.
        //
        // Notify OTHER tabs so they reset to the new profile rather than
        // keeping the previous profile's dashboard data on screen.
        notifyProfileSwitched();
      } finally {
        setIsSwitchingDependant(false);
        setSwitchingTargetId(null);
      }
    },
    []
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user: currentUser,
      accountType: resolveAccountType(currentUser),
      isSwitchingDependant,
      switchingTargetId,
      switchDependant,
      replaceUser: (user: AuthUser) => {
          setCurrentUser(user);
          // notify other hooks/components to refresh their copy
          notifyUserChanged();
        },
    }),
    [currentUser, isSwitchingDependant, switchingTargetId, switchDependant]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
